import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Box,
  Button,
  EmptyState,
  ExpandableSearch,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  ScrollArea,
  Skeleton,
  Text,
} from "@salestrak/ui";
import { DEFAULT_META_LABEL, EntityTag } from "#app/components/entity";
import { PageHeader, PageHeaderSubtitle, PageHeaderTitle, SETTINGS_CONTENT_MAX_WIDTH, SettingsPageGutter } from "#app/components/page-header";
import { SettingsGroup, SettingsGroupHeader, SettingsGroupRow } from "#app/components/settings/settings-group";
import { SettingsGroupRowsSkeleton } from "#app/components/settings/settings-page-skeleton";
import { LoadErrorState } from "#app/components/settings/load-error-state";
import {
  useIssueTypes,
  useIssueTypeMutations,
  type IssueTypeItem,
} from "#app/hooks/settings/use-issue-type-mutations";
import { IssueTypeCreateDialog } from "#app/components/settings/issue-type-create-dialog";
import { IssueTypeDeleteDialog } from "#app/components/settings/issue-type-delete-dialog";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { withInternal } from "#app/components/permissions/require-permission";
import { usePermission } from "#app/lib/hooks/use-permission";

function ProjectTypesList() {
  const { push } = useAppNavigate();
  // Without the manage key the list IS the read-only vocabulary reference —
  // seeing each type's name, icon and description is the whole point, so
  // there's no create, no row menus, and no drill-in (Spencer, 2026-09-10).
  const canManage = usePermission("issue_types.manage");
  const { issueTypes, total, isLoading, error, refetch } = useIssueTypes();
  const {
    createIssueType,
    deleteIssueType,
    duplicateIssueType,
    setDefaultIssueType,
  } = useIssueTypeMutations();

  const [showCreate, setShowCreate] = useState(false);
  const [deletingType, setDeletingType] = useState<IssueTypeItem | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // The list's own search, in its header — not a page bar over the card.
  const [searchQuery, setSearchQuery] = useState("");

  const filteredTypes = useMemo(
    () =>
      issueTypes.filter((t) =>
        t.name.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [issueTypes, searchQuery],
  );

  async function handleCreate(data: { name: string; description: string }) {
    // No icon: the server stamps the picker-set default (DEFAULT_TYPE_ICON).
    const result = await createIssueType({
      name: data.name,
      description: data.description || undefined,
    });
    // A refusal (a taken name) is toasted by the hook; rejecting keeps the
    // dialog open on the typed name.
    if (!result) throw new Error("not created");
    push(`/settings/issue-types/${result.id}`);
  }

  async function handleDelete() {
    if (!deletingType) return;
    await deleteIssueType(deletingType.id);
  }

  async function handleDuplicate(issueType: IssueTypeItem) {
    const copy = await duplicateIssueType(issueType.id);
    if (copy) push(`/settings/issue-types/${copy.issueTypeId}`);
  }

  // The header and card chrome are static, so they render for real while the
  // type list reserves skeleton rows in place (a workspace always has at
  // least its default type).
  const listLoading = isLoading && issueTypes.length === 0;

  if (error && issueTypes.length === 0) {
    return (
      <LoadErrorState
        noun="project types"
        error={error}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  return (
    <>
      <SettingsGroup>
        {/* The page title already names the entity, so the header row carries
            the count, the collapsed search and the create action. The search
            is the list's own tool — it sits with the list's actions, left of
            the plus, and an empty list has nothing to search. */}
        <SettingsGroupHeader>
          {listLoading ? (
            <Skeleton delayed height={12} width={48} />
          ) : (
            <Text size="sm" weight="medium">
              {total} {total === 1 ? "Type" : "Types"}
            </Text>
          )}
          <Box direction="row" align="center" gap="xs">
            {issueTypes.length > 0 ? (
              <ExpandableSearch
                value={searchQuery}
                onValueChange={setSearchQuery}
                size="xs"
                tooltip="Search types"
              />
            ) : null}
            {canManage ? (
              <Button
                aria-label="Create project type"
                variant="ghost"
                size="icon-xs"
                onClick={() => setShowCreate(true)}
              >
                <Icon name="plus" size="md" />
              </Button>
            ) : null}
          </Box>
        </SettingsGroupHeader>

        {listLoading ? (
          <SettingsGroupRowsSkeleton rows={2} />
        ) : filteredTypes.length === 0 ? (
          <Box paddingY="md">
            <EmptyState
              size="xs"
              title={
                searchQuery ? "No matching types" : "No project types yet"
              }
            />
          </Box>
        ) : (
          filteredTypes.map((issueType) => {
            return (
              <SettingsGroupRow
                key={issueType.issueTypeId}
                label={issueType.name}
                // The meta line leads with the use count, then the default
                // designation (a word — a settings row's words belong on its
                // meta line), then the description, joined by the app's
                // " · " meta grammar. An unused type says nothing about use
                // — zero is the absence of a fact, not a fact worth a "0".
                subtitle={
                  [
                    issueType.issueCount > 0
                      ? `${issueType.issueCount.toLocaleString()} ${
                          issueType.issueCount === 1 ? "project" : "projects"
                        }`
                      : null,
                    issueType.isDefault ? DEFAULT_META_LABEL : null,
                    issueType.description,
                  ]
                    .filter(Boolean)
                    .join(" · ") || undefined
                }
                leading={
                  <EntityTag
                    entity={{
                      kind: "issueType",
                      id: issueType.issueTypeId,
                      issueType: {
                        name: issueType.name,
                        icon: { icon: issueType.icon?.icon },
                      },
                    }}
                    display="visual"
                    // The settings-row leading rail is 32px — the same well
                    // SettingsGroupRow `icon` renders and the API-key rows'
                    // avatars use — but the glyph holds the settings 16px
                    // rung (`sm`), matching IconTile's 32px/16px grammar;
                    // `md` runs the identity glyph up to 20px.
                    size="sm"
                    visualBoxSize={32}
                    padded
                    interactive={false}
                  />
                }
                onClick={
                  canManage
                    ? () => push(`/settings/issue-types/${issueType.id}`)
                    : undefined
                }
                // The trail is the "…" menu on every row; a chevron
                // beside it would sit in the same spot and say what the
                // row's own hover wash already says.
                chevron={false}
                actionsVisible={openMenuId === issueType.issueTypeId}
              >
                {canManage ? (
                <Menu onOpenChange={(open) => setOpenMenuId(open ? issueType.issueTypeId : null)}>
                  <MenuTrigger
                    render={
                      <Button
                        aria-label={`Actions for ${issueType.name}`}
                        variant="ghost"
                        size="icon-xs"
                      />
                    }
                  >
                    <Icon name="ellipsis" size="md" />
                  </MenuTrigger>
                  <MenuPopup align="end" className="min-w-45">
                    <MenuItem onClick={() => handleDuplicate(issueType)}>
                      <Icon name="copy" size="md" />
                      Duplicate
                    </MenuItem>
                    {/* The default type is where a deleted type's projects
                        land, so it offers no delete; promoting another
                        type is the way out of default. */}
                    {!issueType.isDefault ? (
                      <>
                        <MenuItem onClick={() => setDefaultIssueType(issueType.id)}>
                          <Icon name="check" size="md" />
                          Set as default
                        </MenuItem>
                        <MenuItem
                          onClick={() => setDeletingType(issueType)}
                        >
                          <Icon name="trash" size="md" />
                          Delete type...
                        </MenuItem>
                      </>
                    ) : null}
                  </MenuPopup>
                </Menu>
                ) : null}
              </SettingsGroupRow>
            );
          })
        )}
      </SettingsGroup>

      <IssueTypeCreateDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        onSubmit={handleCreate}
      />

      <IssueTypeDeleteDialog
        open={deletingType !== null}
        onOpenChange={(open) => { if (!open) setDeletingType(null); }}
        issueTypeName={deletingType?.name ?? ""}
        issueCount={deletingType?.issueCount ?? 0}
        onConfirm={handleDelete}
      />
    </>
  );
}

function TypesPage() {
  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderTitle>Types</PageHeaderTitle>
          <PageHeaderSubtitle>
            Define the Types Members give Projects, with the layout and required properties of each.
          </PageHeaderSubtitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="lg" fill="width" maxWidth={SETTINGS_CONTENT_MAX_WIDTH}>
            <ProjectTypesList />

            <SettingsPageGutter />
          </Box>
        </Box>
      </ScrollArea>
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/issue-types/")({
  // Read-only vocabulary page for internal viewers without the manage key —
  // the list drops its edit affordances; only guests are denied.
  component: withInternal(TypesPage),
});
